/**
 * localStorage-based feature flag utility.
 *
 * Import and use in code:
 *   featureFlags.read('PERSONA_CRUD_ENABLED')
 *
 * Toggle from the browser console (page reloads automatically):
 *   featureFlags.enable('PERSONA_CRUD_ENABLED')
 *   featureFlags.disable('PERSONA_CRUD_ENABLED')
 *   featureFlags.reset('PERSONA_CRUD_ENABLED')
 *   featureFlags.list()
 */

/**
 * Add new feature flags here with their default values.
 *
 * NOTE: this DIY registry is being phased out in favour of OpenFeature
 * (Statsig). For NEW flags use `useBooleanFlagValue` from
 * `@openfeature/react-sdk` and add the key to `KNOWN_OPENFEATURE_FLAGS`
 * in `openfeature-flags.ts` — see CLAUDE.md "Feature Flags". The flags
 * below are pre-existing entries owned by individual features and will
 * be migrated by their respective owners.
 */
const FEATURE_FLAGS = {
  PERSONA_DETAIL_V2_ENABLED: false,
} as const satisfies Record<string, boolean>;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

const registry: Partial<Record<FeatureFlag, boolean>> = {};

export const featureFlags = {
  /** Checks localStorage for an override before using the default value. */
  read(name: FeatureFlag, defaultValue?: boolean): boolean {
    const stored = localStorage.getItem(`ff:${name}`);
    const value = stored !== null ? stored === 'true' : (defaultValue ?? FEATURE_FLAGS[name]);
    registry[name] = value;
    return value;
  },
  enable(name: FeatureFlag) {
    // eslint-disable-next-line i18next/no-literal-string -- localStorage value
    localStorage.setItem(`ff:${name}`, 'true');
    window.location.reload();
  },
  disable(name: FeatureFlag) {
    // eslint-disable-next-line i18next/no-literal-string -- localStorage value
    localStorage.setItem(`ff:${name}`, 'false');
    window.location.reload();
  },
  reset(name: FeatureFlag) {
    localStorage.removeItem(`ff:${name}`);
    window.location.reload();
  },
  list() {
    return registry;
  },
};

window.featureFlags = featureFlags;
